import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/account")({
  component: AccountPage,
});

type Status = { kind: "ok" | "err"; msg: string } | null;

function Banner({ status }: { status: Status }) {
  if (!status) return null;
  const base = "mt-3 rounded-lg border px-3 py-2 text-sm";
  if (status.kind === "ok") {
    return (
      <p
        className={`${base} border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400`}
      >
        {status.msg}
      </p>
    );
  }
  return (
    <p className={`${base} border-destructive/30 bg-destructive/10 text-destructive`}>
      {status.msg}
    </p>
  );
}

function AccountPage() {
  const router = useRouter();

  const [loaded, setLoaded] = useState(false);
  const [currentEmail, setCurrentEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");

  const [profileSaving, setProfileSaving] = useState(false);
  const [profileStatus, setProfileStatus] = useState<Status>(null);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);
  const [pwStatus, setPwStatus] = useState<Status>(null);

  const [signOutEverywhereLoading, setSignOutEverywhereLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const u = data.user;
      if (u) {
        const meta = (u.user_metadata ?? {}) as {
          display_name?: string;
          full_name?: string;
          name?: string;
        };
        setDisplayName(meta.display_name ?? meta.full_name ?? meta.name ?? "");
        setCurrentEmail(u.email ?? "");
        setEmail(u.email ?? "");
      }
      setLoaded(true);
    });
  }, []);

  const onSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileStatus(null);
    setProfileSaving(true);
    try {
      const emailChanged = email.trim().toLowerCase() !== currentEmail.toLowerCase();
      const payload: { data: { display_name: string }; email?: string } = {
        data: { display_name: displayName.trim() },
      };
      if (emailChanged) payload.email = email.trim();
      const { error } = await supabase.auth.updateUser(payload);
      if (error) throw error;
      setProfileStatus({
        kind: "ok",
        msg: emailChanged
          ? "Profile saved. Check both your old and new inbox to confirm the email change."
          : "Profile saved.",
      });
    } catch (err) {
      setProfileStatus({
        kind: "err",
        msg: err instanceof Error ? err.message : "Failed to save profile.",
      });
    } finally {
      setProfileSaving(false);
    }
  };

  const onSavePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwStatus(null);
    if (newPassword.length < 8) {
      setPwStatus({ kind: "err", msg: "Password must be at least 8 characters." });
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwStatus({ kind: "err", msg: "Passwords do not match." });
      return;
    }
    setPwSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      setNewPassword("");
      setConfirmPassword("");
      setPwStatus({ kind: "ok", msg: "Password updated." });
    } catch (err) {
      setPwStatus({
        kind: "err",
        msg: err instanceof Error ? err.message : "Failed to update password.",
      });
    } finally {
      setPwSaving(false);
    }
  };

  const onSignOutEverywhere = async () => {
    if (!confirm("Sign out of all devices and browsers?")) return;
    setSignOutEverywhereLoading(true);
    try {
      await supabase.auth.signOut({ scope: "global" });
      router.navigate({ to: "/admin/login" });
    } finally {
      setSignOutEverywhereLoading(false);
    }
  };

  return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">
        Account
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        Manage your profile, email, and password.
      </p>

      <section className="mt-8 rounded-2xl border border-border bg-card/30 p-6">
        <h2 className="font-display text-lg font-semibold text-foreground">Profile</h2>
        <form onSubmit={onSaveProfile} className="mt-4 space-y-4">
          <div>
            <label
              htmlFor="display_name"
              className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground"
            >
              Display name
            </label>
            <input
              id="display_name"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Your name"
              disabled={!loaded}
              className="mt-2 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground focus:border-foreground focus:outline-none"
            />
          </div>
          <div>
            <label
              htmlFor="email"
              className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={!loaded}
              className="mt-2 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground focus:border-foreground focus:outline-none"
            />
            <p className="mt-1.5 text-xs text-muted-foreground">
              Changing your email sends a confirmation link to both your old and new addresses.
            </p>
          </div>
          <div className="flex items-center justify-between">
            <button
              type="submit"
              disabled={profileSaving || !loaded}
              className="rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {profileSaving ? "Saving…" : "Save profile"}
            </button>
          </div>
          <Banner status={profileStatus} />
        </form>
      </section>

      <section className="mt-8 rounded-2xl border border-border bg-card/30 p-6">
        <h2 className="font-display text-lg font-semibold text-foreground">Change password</h2>
        <form onSubmit={onSavePassword} className="mt-4 space-y-4">
          <div>
            <label
              htmlFor="new_password"
              className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground"
            >
              New password
            </label>
            <input
              id="new_password"
              type="password"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="mt-2 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground focus:border-foreground focus:outline-none"
            />
          </div>
          <div>
            <label
              htmlFor="confirm_password"
              className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground"
            >
              Confirm new password
            </label>
            <input
              id="confirm_password"
              type="password"
              required
              minLength={8}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="mt-2 w-full rounded-xl border border-border bg-background px-4 py-2.5 text-sm text-foreground focus:border-foreground focus:outline-none"
            />
          </div>
          <button
            type="submit"
            disabled={pwSaving}
            className="rounded-full bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {pwSaving ? "Updating…" : "Update password"}
          </button>
          <Banner status={pwStatus} />
        </form>
      </section>

      <section className="mt-8 rounded-2xl border border-border bg-card/30 p-6">
        <h2 className="font-display text-lg font-semibold text-foreground">Sessions</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Sign out of every device and browser where this account is currently signed in.
        </p>
        <button
          onClick={onSignOutEverywhere}
          disabled={signOutEverywhereLoading}
          className="mt-4 rounded-full border border-border px-4 py-2 text-xs font-medium text-foreground transition-colors hover:border-destructive hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
        >
          {signOutEverywhereLoading ? "Signing out…" : "Sign out everywhere"}
        </button>
      </section>
    </div>
  );
}
